import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, Mail } from 'lucide-react';
import { adminFetch, isAdminAuthenticated } from '@/utils/adminAuth';

export const AdminCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [mailingList, setMailingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('customers');
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminAuthenticated()) {
      navigate('/admin');
      return;
    }
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersRes, mailingRes] = await Promise.all([
        adminFetch('/admin/customers'),
        adminFetch('/admin/mailing-list')
      ]);
      const customersData = await customersRes.json();
      const mailingData = await mailingRes.json();
      setCustomers(customersData);
      setMailingList(mailingData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background noise-texture py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="p-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-4xl font-bold font-display text-foreground">Customers</h1>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setTab('customers')}
            className={`px-6 py-3 rounded-full font-bold transition-all ${
              tab === 'customers'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-muted-foreground hover:text-foreground'
            }`}
          >
            Customers ({customers.length})
          </button>
          <button
            onClick={() => setTab('mailing')}
            className={`px-6 py-3 rounded-full font-bold transition-all ${
              tab === 'mailing'
                ? 'bg-primary text-primary-foreground'
                : 'bg-card text-muted-foreground hover:text-foreground'
            }`}
          >
            Mailing List ({mailingList.length})
          </button>
        </div>

        {/* Customers Table */}
        {tab === 'customers' && (
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Email</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-foreground">Purchases</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-foreground">Total Spent</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-foreground">Last Purchase</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {customers.map((customer, index) => (
                    <tr key={index} className="hover:bg-background/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-primary font-bold">
                              {customer.email[0].toUpperCase()}
                            </span>
                          </div>
                          <span className="text-sm font-semibold text-foreground">
                            {customer.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-muted-foreground">
                        {customer.total_purchases}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-bold text-primary">
                          ${customer.total_spent.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-muted-foreground">
                        {new Date(customer.last_purchase).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {customers.length === 0 && (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No customers yet</p>
              </div>
            )}
          </div>
        )}

        {/* Mailing List Table */}
        {tab === 'mailing' && (
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background border-b border-border">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-foreground">Name</th>
                    <th className="px-6 py-4 text-right text-sm font-bold text-foreground">Subscribed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {mailingList.map((subscriber, index) => (
                    <tr key={index} className="hover:bg-background/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Mail className="w-5 h-5 text-primary" />
                          <span className="text-sm font-semibold text-foreground">
                            {subscriber.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-muted-foreground">
                        {subscriber.name || '-'}
                      </td>
                      <td className="px-6 py-4 text-right text-sm text-muted-foreground">
                        {new Date(subscriber.subscribed_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {mailingList.length === 0 && (
              <div className="text-center py-12">
                <Mail className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                <p className="text-muted-foreground">No subscribers yet</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};